import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from "@angular/core";
import { AbstractControl, UntypedFormArray, UntypedFormGroup, ValidatorFn } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatTabGroup } from "@angular/material/tabs";
import { map, Observable, startWith, Subscription } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import {
	AssessmentEvaludationReq,
	CandidateRating,
	DraftItem,
	OBAttempt,
	OBComments,
	OBCommentsReq,
	OutRow,
	ResetOB,
} from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { UtilityService } from "src/app/util/utility.service";
import { ContainerRatingsService } from "../../services/base/container-ratings.services";

@Component({
	selector: "app-add-maneuver-form",
	templateUrl: "./add-maneuver-form.component.html",
	styleUrls: ["./add-maneuver-form.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AddManeuverFormComponent implements OnInit, OnDestroy, OnChanges {
	maneuverForm!: UntypedFormGroup;
	parentForm!: UntypedFormArray;
	parentFormArrayIndex: number = 0;
	panelOpenState: boolean = false;
	currentSliderValue: number = 0;
	loadingCount: number = 0;
	resetRepeat: number = 0;
	isTS1 = false;
	isCCForm = false;
	isCabinCrewForm = false;

	@Input() isManeuverVisible = true;
	@Input() isSafetyOutcomeVisible = false;
	@Input() tabIndex!: MatTabGroup;
	@Input() obComments!: Array<OBComments>;
	@Input() showExpansion: boolean = true;
	@Input("maneuverForm") set _(value: any) {
		this.maneuverForm = value as UntypedFormGroup;
	}
	@Input() maneuverAction?: "create-form" | "edit-form";

	@Output() selectedOBList: EventEmitter<any> = new EventEmitter<any>();
	@Output() resetCurrentManeuver: EventEmitter<any> = new EventEmitter<any>();

	OB_LIST: Array<any> = [];
	obSections: Record<string, UntypedFormGroup> = {};
	tempFrom: any = {};
	hasValueList: any = [];
	newOBList: any = []; // OB's added on rating

	disableSafetyOutcome$!: Observable<boolean>;
	private subs = new Subscription();

	constructor(
		public sharedService: SharedService,
		private dialog: MatDialog,
		private httpService: HttpService,
		private loadingService: LoaderService,
		private cd: ChangeDetectorRef,
		private util: UtilityService,
		private containerRatingsService: ContainerRatingsService,
	) {}

	// Ensure we never operate on {} — always return a fully shaped CandidateRating
	private getSafeCandidateRating(record?: DraftItem): CandidateRating {
		const cr = (record?.candidateRating ?? {}) as Partial<CandidateRating>;
		return {
			temEvaluations: cr.temEvaluations ?? [],
			temRate: cr.temRate ?? {},
			addRemoveOb: cr.addRemoveOb ?? [],
			otherComments: cr.otherComments ?? [],
			resetManeuvers: cr.resetManeuvers ?? [],
			selectedOBs: cr.selectedOBs ?? [],
		};
	}

	// Validator: forbid a specific value when the control is enabled.
	private notEqualsWhenEnabled =
		(bad: any): ValidatorFn =>
		(c: AbstractControl) =>
			c.disabled || c.value !== bad ? null : { disallowedValue: true };

	ngOnInit(): void {
		this.parentForm = this.maneuverForm.parent as UntypedFormArray;
		this.parentFormArrayIndex = this.parentForm.controls.findIndex((subform) => subform === this.maneuverForm);

		this.OB_LIST = this.transformObservations(this.containerRatingsService.getObservationSnapshot());

		// Build a map of OB title -> FormGroup
		this.obSections = this.OB_LIST.reduce(
			(acc, ob) => {
				const grp = this.maneuverForm?.get(ob.title);
				if (grp instanceof UntypedFormGroup) acc[ob.title] = grp;
				return acc;
			},
			{} as Record<string, UntypedFormGroup>,
		);

		this.isTS1 = this.containerRatingsService.formType == CONSTANT.FORM_TYPE.ts_1;
		this.isCCForm =
			this.containerRatingsService.formType == CONSTANT.FORM_TYPE.cabin_crew ||
			this.containerRatingsService.formType == CONSTANT.FORM_TYPE.cabin_crew_instructor;

		this.isCabinCrewForm = this.containerRatingsService.formType == CONSTANT.FORM_TYPE.cabin_crew;

		// Forward selected OBs upstream
		this.selectedOBs?.valueChanges.subscribe((val) => {
			if (!val) return;
			if (Array.isArray(val)) {
				for (let i = 0; i < val.length; i++) {
					const v = val[i];

					// group key: prefer maneuverCode; fall back to maneuver text
					const curKey = v?.maneuver ?? "";
					const next = val[i + 1];
					const nextKey = next?.maneuver ?? "";

					const isLastOfThisManeuver = !next || nextKey !== curKey;

					if (isLastOfThisManeuver && v?.isRepeat === true) {
						const obAction: OBComments = {
							maneuver: typeof v?.maneuver === "string" ? v.maneuver : "",
							ob: "REPEAT",
							remarks: "REPEAT",
							action: false,
							instructorComment: "",
						} as OBComments;

						this.selectedOBList.emit(v);

						this.selectedOBList.emit(obAction);
					} else {
						// always emit the original record
						this.selectedOBList.emit(v);
					}
				}
			} else {
				this.selectedOBList.emit(val);
			}
		});

		// true when EVERY enabled OB total is 0 -> disable TEM Safety Outcome
		this.disableSafetyOutcome$ = this.maneuverForm.valueChanges.pipe(
			startWith(this.maneuverForm.getRawValue()),
			map(() => this.areAllObTotalsZero()),
		);

		// initial validator/enable state for safetyOutcome
		this.configureSafetyOutcomeValidation();

		const safety = this.safetyOutcome;
		if (safety) {
			this.subs.add(
				this.disableSafetyOutcome$.subscribe((allZero) => {
					if (allZero) {
						if (!safety.disabled) safety.disable({ emitEvent: false });
						if (safety.value !== -2) safety.patchValue(-2, { emitEvent: false });
					} else {
						if (safety.disabled) safety.enable({ emitEvent: false });
					}
					safety.updateValueAndValidity({ emitEvent: false });
				}),
			);

			// Enforce initial state from current values
			if (this.areAllObTotalsZero()) {
				safety.disable({ emitEvent: false });
				if (safety.value !== -2) safety.patchValue(-2, { emitEvent: false });
			} else {
				safety.enable({ emitEvent: false });
			}
			safety.updateValueAndValidity({ emitEvent: false });
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["isSafetyOutcomeVisible"] && !changes["isSafetyOutcomeVisible"].firstChange) {
			this.configureSafetyOutcomeValidation();
		}
	}

	private configureSafetyOutcomeValidation(): void {
		const safety = this.safetyOutcome;
		if (!safety) return;

		safety.clearValidators();

		if (this.isSafetyOutcomeVisible) {
			// When visible: -2 is INVALID if the control is enabled
			safety.addValidators(this.notEqualsWhenEnabled(-2));
		} else {
			// When hidden: allow -2 and park/disable it
			if (safety.value !== -2) safety.patchValue(-2, { emitEvent: false });
			if (!safety.disabled) safety.disable({ emitEvent: false });
		}

		safety.updateValueAndValidity({ emitEvent: false });
	}

	/** true if EVERY OB group totals to 0 and none were interacted with (ignoring rows marked isDisabled) */
	private areAllObTotalsZero(): boolean {
		if (!this.obSections || Object.keys(this.obSections).length === 0) return true;

		for (const [, group] of Object.entries(this.obSections) as [string, UntypedFormGroup][]) {
			const raw = group.getRawValue(); // includes disabled controls
			for (const [, value] of Object.entries(raw)) {
				const count = (value as any)?.currentCount ?? 0;
				const isDisabled = !!(value as any)?.isDisabled;
				const totalCount = (value as any)?.totalCount ?? 0;
				if (!isDisabled && (count !== 0 || totalCount !== 0)) return false;
			}
		}
		return true;
	}

	get inDraftMode(): boolean {
		return this.containerRatingsService.inDraftMode;
	}

	ngAfterContentChecked(): void {
		if (this.maneuverForm.touched && this.loadingCount == 1) {
			this.tempFrom = Object.assign({}, this.maneuverForm.getRawValue());

			this.OB_LIST.forEach((ob: any, index: number) => {
				this.hasValueList[index] = this.hasValue(ob.title);
			});
			this.cd.detectChanges();
		}
		this.loadingCount++;
	}

	hasValue(key: string): boolean {
		const codeValues = this.maneuverForm.value[key];
		for (const k in codeValues) {
			if (codeValues[k].currentCount > 0 && !codeValues[k].isDisabled) return true;
		}
		return false;
	}

	confirmationModal(data: AssessmentEvaludationReq): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "logout-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Confirmation",
				modalContent: "Are you sure?",
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result && result.close) {
				this.ratingChangeHandler(data);
			} else {
				// leave enforcement to the validator now
				this.safetyOutcome?.patchValue(undefined);
				this.safetyOutcome?.setValue(undefined);
				this.maneuverForm.setErrors({ invalid: true });
			}
			sub.unsubscribe();
		});
	}

	temCommentsModal(data: AssessmentEvaludationReq): void {
		let remarks = data.rate === 0 ? "Incomplete" : data.rate === -1 ? "N/A" : `Rating ${data.rate}`;

		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "tem-comments-modal",
			height: "560px",
			width: "1154px",
			hasBackdrop: true,
			data: {
				modalName: "temCommentsModal",
				showSwitch: this.containerRatingsService.formType == CONSTANT.FORM_TYPE.ts_1 ? true : false,
				modalTitle: "Remarks",
				modalBtnConfirmText: "Save",
				modalBtnCancelText: "Cancel",
				disableClose: true,
				temComment: {
					rating: data.rate,
					remarks: remarks,
					maneuver:
						this.maneuver.value.toLowerCase() != CONSTANT.DUMMY_PLACEHOLDER.toLowerCase() ? this.maneuver.value : "",
				},
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result && result.close) {
				const remakrs = result?.comment ?? "";
				if (data.rate != -2) {
					const selectedOB: OBComments = {
						maneuver:
							this.repeat?.value != 0 ? `R${this.repeat?.value} ${this.maneuver.value}` : `${this.maneuver.value}`,
						ob: "TEM",
						action: true,
						remarks: remarks,
						instructorComment: remakrs.toUpperCase(),
						candidateId: this.candidateId.value,
						maneuverCode: this.maneuverCode.value,
						rate: data.rate,
						isDisabled: false,
						candidateRole:
							this.containerRatingsService.formType != CONSTANT.FORM_TYPE.ts_1
								? CandidateRole.NONE
								: result.pilotSwitch === CONSTANT.CANDIDATE_ROLE.PF
									? CandidateRole.PF
									: CandidateRole.PM,
					};

					this.safetyOutcome?.patchValue(data.rate);

					// Use safe CR
					const record: DraftItem | undefined = this.containerRatingsService.draft;
					const cr = this.getSafeCandidateRating(record);

					const temEvaluations = [
						...cr.temEvaluations,
						{
							assessmentId: this.assessmentId.value,
							candidateId: this.candidateId.value,
							data: {
								...data,
								candidateRole:
									this.containerRatingsService.formType != CONSTANT.FORM_TYPE.ts_1
										? CandidateRole.NONE
										: result.pilotSwitch === CONSTANT.CANDIDATE_ROLE.PF
											? CandidateRole.PF
											: CandidateRole.PM,
							},
							remarks: remakrs.toUpperCase(),
							createdAt: new Date().toISOString(),
						},
					];

					const selectedOBs = [
						...cr.selectedOBs,
						{
							...selectedOB,
							candidateId: this.candidateId.value,
						},
					];

					const temRate = {
						...cr.temRate,
						[`${this.candidateId.value}.${this.assessmentId.value}`]: {
							assessmentId: this.assessmentId.value,
							rate: data.rate,
						},
					};

					this.containerRatingsService.updateSelectedDraft({
						candidateRating: {
							...cr,
							temEvaluations,
							selectedOBs,
							temRate,
						},
						lastUpdatedDate: new Date().toISOString(),
						continuePage: 2,
					});

					this.selectedOBList.emit(selectedOB);
				} else {
					this.maneuverForm.setErrors({ invalid: true });
				}
			} else {
				this.safetyOutcome?.patchValue(undefined);
				this.safetyOutcome?.setValue(undefined);
				this.maneuverForm.setErrors({ invalid: true });
			}
			sub.unsubscribe();
		});
	}

	onRatingChange(value: any) {
		const data = {
			remarks: "",
			rate: value,
			assessmentId: this.assessmentId.value,
		} as AssessmentEvaludationReq;

		data.remarks = data.rate === -1 ? "N/A" : "";

		if ([0, 1, 5].includes(value)) {
			this.temCommentsModal(data);
		} else {
			if (value == -2) {
				// Let the validator enforce if this is allowed
				this.safetyOutcome?.patchValue(data.rate);
				this.ratingChangeHandler(data);
				this.parentForm.updateValueAndValidity();
			} else {
				if (value == -1) {
					this.confirmationModal(data);
				} else {
					this.ratingChangeHandler(data);
				}
			}
		}
	}

	ratingChangeHandler(data: AssessmentEvaludationReq): void {
		this.safetyOutcome?.patchValue(data.rate);

		const record: DraftItem | undefined = this.containerRatingsService.draft;
		const cr = this.getSafeCandidateRating(record);

		const temEvaluations = [
			...cr.temEvaluations,
			{
				assessmentId: this.assessmentId.value,
				candidateId: this.candidateId.value,
				data,
				remarks: data.remarks,
				createdAt: new Date().toISOString(),
			},
		];

		const temRate = {
			...cr.temRate,
			[`${this.candidateId.value}.${this.assessmentId.value}`]: {
				assessmentId: this.assessmentId.value,
				rate: data.rate,
			},
		};

		this.containerRatingsService.updateSelectedDraft({
			candidateRating: {
				...cr,
				temEvaluations,
				temRate,
			},
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});
	}

	transformObservations(rows: any, sortKeyForTitle?: (title: string, index1Based: number) => number): OutRow[] {
		const groups = new Map<string, { desc: string; items: Array<{ description: string; num: number }> }>();

		for (const r of rows ?? []) {
			const title = r.observation_category.description;
			const desc = r.observation_category.sub_description;
			if (!groups.has(title)) {
				groups.set(title, { desc, items: [] });
			}
			// Extract the numerical part from the code or name (e.g., "COM_2" -> 2, "COM 2" -> 2, "COM2" -> 2)
			const code = (r.code || r.name || "").trim();
			// Try to match formats: "COM_2", "COM 2", "COM2"
			const match1 = /^[a-zA-Z]+_(\d+)$/.exec(code); // "COM_2" format
			const match2 = /^[a-zA-Z]+\s+(\d+)$/.exec(code); // "COM 2" format
			const match3 = /^[a-zA-Z]+(\d+)$/.exec(code); // "COM2" format
			const num = match1
				? parseInt(match1[1], 10)
				: match2
					? parseInt(match2[1], 10)
					: match3
						? parseInt(match3[1], 10)
						: 0;
			groups.get(title)!.items.push({ description: r.description, num });
		}

		const out: OutRow[] = [];
		let idx = 0;
		for (const [title, g] of groups.entries()) {
			idx += 1;
			// Sort items by their numerical part to match form control ordering (COM1, COM2, ..., COM9, COM10)
			g.items.sort((a, b) => a.num - b.num);
			out.push({
				title,
				desc: g.desc,
				item_desc: g.items.map((item) => item.description),
				sortKey: sortKeyForTitle ? sortKeyForTitle(title, idx) : idx,
			});
		}
		return out;
	}

	/**
	 * Builds an observation code (e.g., "com_10") from a form control key (e.g., "com 10" or "com10").
	 * Replaces the old `slice(0,3)_slice(4,5)` pattern which truncated double-digit numbers.
	 */
	private buildObservationCode(formattedOB: string): string {
		if (this.isCCForm) {
			const spaceIdx = formattedOB.indexOf(" ");
			const numPart = spaceIdx >= 0 ? formattedOB.slice(spaceIdx + 1) : formattedOB.replace(/^[a-z]+/, "");
			return `${this.maneuverCode.value}_${numPart}`;
		}
		const spaceIdx = formattedOB.indexOf(" ");
		if (spaceIdx >= 0) {
			return `${formattedOB.slice(0, spaceIdx)}_${formattedOB.slice(spaceIdx + 1)}`;
		}
		const match = /^([a-z]+)(\d+)$/.exec(formattedOB);
		if (match) {
			return `${match[1]}_${match[2]}`;
		}
		return formattedOB;
	}

	get maneuver() {
		return this.maneuverForm.get("maneuver")!;
	}
	get maneuverCode() {
		return this.maneuverForm.get("maneuverCode")!;
	}
	get assessmentId() {
		return this.maneuverForm.get("assessmentId")!;
	}
	get selectedOBs() {
		return this.maneuverForm.get("selectedOBs")!;
	}
	get traineeAssessmentId() {
		return this.maneuverForm.get("traineeAssessmentId")!;
	}
	get candidateId() {
		return this.maneuverForm.get("candidateId")!;
	}
	get safetyOutcome() {
		return this.maneuverForm.get("safetyOutcome");
	}
	get repeat() {
		return this.maneuverForm.get("repeat");
	}

	saveManeuver(value: string): void {
		this.maneuver.patchValue(value);
	}

	onCheckBoxClick(event: [AbstractControl | null, string, boolean, OBAttempt, boolean, boolean]): void {
		const [control, ob, isAdded, value, disabled, isFromNAEvent] = event;

		if (isFromNAEvent) {
			this.wranglingAction(control, ob, isAdded, value, disabled);
		} else {
			this.obCommentsModel(control, ob, isAdded, value);
		}
	}

	repeatConfirmModal(): void {
		const type = this.isCabinCrewForm ? "Competency" : "Maneuver";
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "logout-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Confirm repeat action!",
				modalContent: `Are you sure to reset this ${type} to default selection?`,
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result?.close) {
				this.saveAndClear();
			}
			sub.unsubscribe();
		});
	}

	obCommentsModel(action: AbstractControl | null, ob: string, isAdded: boolean, newActionValue: OBAttempt): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "ob-comments-modal",
			height: "560px",
			width: "1154px",
			hasBackdrop: true,
			data: {
				modalName: "obCommentsModel",
				modalTitle: "Summary",
				showSwitch: this.containerRatingsService.formType == CONSTANT.FORM_TYPE.ts_1 ? true : false,
				modalContent: this.obComments,
				modalBtnConfirmText: "Save",
				modalBtnCancelText: "Cancel",
				disableClose: true,
				newComment: {
					maneuver:
						this.maneuver.value.toLowerCase() != CONSTANT.DUMMY_PLACEHOLDER.toLowerCase() ? this.maneuver.value : "",
					ob: ob,
					action: isAdded,
				},
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result && result.comment) {
				let formattedOB = ob.toLocaleLowerCase();

				const obAction = {
					maneuver:
						this.repeat?.value != 0
							? `R${this.repeat?.value} ${
									this.maneuver.value.toLowerCase() != CONSTANT.DUMMY_PLACEHOLDER.toLowerCase()
										? this.isCabinCrewForm
											? " "
											: this.maneuver.value
										: ""
								}`
							: `${
									this.maneuver.value.toLowerCase() != CONSTANT.DUMMY_PLACEHOLDER.toLowerCase()
										? this.isCabinCrewForm
											? " "
											: this.maneuver.value
										: ""
								}`,
					ob: ob,
					action: isAdded,
					instructorComment: result.comment.toUpperCase(),
					commentId: "",
					maneuverCode: this.maneuverCode.value,
					observationCode: this.buildObservationCode(formattedOB),
					candidateId: this.candidateId.value,
					candidateRole:
						this.containerRatingsService.formType != CONSTANT.FORM_TYPE.ts_1
							? CandidateRole.NONE
							: result.pilotSwitch === CONSTANT.CANDIDATE_ROLE.PF
								? CandidateRole.PF
								: CandidateRole.PM,
					isDisabled: false,
				} as OBComments;

				const obActionReq = {
					candidateId: this.candidateId.value,
					observationCode: this.buildObservationCode(formattedOB),
					maneuverCode: this.maneuverCode.value,
					isRemoved: !isAdded,
					description: result.comment.toUpperCase(),
					activeAttempt: newActionValue?.currentCount ?? 0,
					totalAttempt: newActionValue?.totalCount ?? 0,
					candidateRole:
						this.containerRatingsService.formType != CONSTANT.FORM_TYPE.ts_1
							? CandidateRole.NONE
							: result.pilotSwitch === CONSTANT.CANDIDATE_ROLE.PF
								? CandidateRole.PF
								: CandidateRole.PM,
					isDisabled: false,
					createdAt: new Date().toISOString(),
				} as OBCommentsReq;

				action?.patchValue(newActionValue);

				if (!isAdded) {
					const newOB = {} as any;
					newOB[ob] = isAdded;
					this.newOBList.push(newOB);
				}

				const record: DraftItem | undefined = this.containerRatingsService.draft;
				const cr = this.getSafeCandidateRating(record);

				const addRemoveOb = [
					...cr.addRemoveOb,
					{
						...obActionReq,
						candidateId: this.candidateId.value,
					},
				];

				const selectedOBs = [
					...cr.selectedOBs,
					{
						...obAction,
						candidateId: this.candidateId.value,
					},
				];

				this.containerRatingsService.updateSelectedDraft({
					candidateRating: { ...cr, addRemoveOb, selectedOBs },
					lastUpdatedDate: new Date().toISOString(),
					continuePage: 2,
				});

				// this.containerRatingsService.addToBeSynched("observation", obActionReq);
				this.selectedOBList.emit(obAction);
			}
			sub.unsubscribe();
		});
	}

	wranglingAction(
		action: AbstractControl | null,
		ob: string,
		isAdded: boolean | null,
		newActionValue: OBAttempt,
		disabled: boolean,
	): void {
		const record: DraftItem | undefined = this.containerRatingsService.draft;
		const cr = this.getSafeCandidateRating(record);

		let lastNonDisabledAction: boolean | null = null;
		let lastRole: any = CandidateRole.NONE;
		const lastActionOfSelectedObNotNull = cr.selectedOBs.filter(
			(observation: any) => observation.action != null && observation.ob == ob,
		);
		if (lastActionOfSelectedObNotNull.length > 0) {
			lastNonDisabledAction = lastActionOfSelectedObNotNull?.[lastActionOfSelectedObNotNull.length - 1].action;

			lastRole = lastActionOfSelectedObNotNull?.[lastActionOfSelectedObNotNull.length - 1].candidateRole;
		}

		let formattedOB = ob.toLocaleLowerCase();

		const obAction = {
			maneuver:
				this.repeat?.value != 0
					? `R${this.repeat?.value} ${
							this.maneuver.value.toLowerCase() != CONSTANT.DUMMY_PLACEHOLDER.toLowerCase()
								? this.isCabinCrewForm
									? " "
									: this.maneuver.value
								: ""
						}`
					: `${
							this.maneuver.value.toLowerCase() != CONSTANT.DUMMY_PLACEHOLDER.toLowerCase()
								? this.isCabinCrewForm
									? " "
									: this.maneuver.value
								: ""
						}`,
			ob: ob,
			action: !disabled ? lastNonDisabledAction : null,
			instructorComment: "",
			commentId: "",
			maneuverCode: this.maneuverCode.value,
			observationCode: this.buildObservationCode(formattedOB),
			candidateId: this.candidateId.value,
			candidateRole: this.containerRatingsService.formType != CONSTANT.FORM_TYPE.ts_1 ? CandidateRole.NONE : lastRole,
			isDisabled: disabled,
		} as OBComments;

		const obActionReq = {
			candidateId: this.candidateId.value,
			observationCode: this.buildObservationCode(formattedOB),
			maneuverCode: this.maneuverCode.value,
			isRemoved: !disabled ? (lastNonDisabledAction != null ? !lastNonDisabledAction : null) : null,
			description: disabled ? CONSTANT.NOT_APPLICABLE_NOTES : CONSTANT.RESET_NOT_APPLICABLE_NOTES,
			activeAttempt: newActionValue?.currentCount ?? 0,
			totalAttempt: newActionValue?.totalCount ?? 0,
			candidateRole: this.containerRatingsService.formType != CONSTANT.FORM_TYPE.ts_1 ? CandidateRole.NONE : lastRole,
			isDisabled: disabled,
			createdAt: new Date().toISOString(),
		} as OBCommentsReq;

		newActionValue.lastAction =
			lastNonDisabledAction != null
				? lastNonDisabledAction
					? CONSTANT.OB_LAST_ACTION.ADDED
					: CONSTANT.OB_LAST_ACTION.REMOVED
				: CONSTANT.OB_LAST_ACTION.NO_ACTION;

		action?.patchValue(newActionValue);

		if (!isAdded && isAdded != null) {
			const newOB = {} as any;
			newOB[ob] = isAdded;
			this.newOBList.push(newOB);
		}

		const addRemoveOb = [
			...cr.addRemoveOb,
			{
				...obActionReq,
				candidateId: this.candidateId.value,
			},
		];

		const selectedOBs = [
			...cr.selectedOBs,
			{
				...obAction,
				candidateId: this.candidateId.value,
			},
		];

		this.containerRatingsService.updateSelectedDraft({
			candidateRating: { ...cr, addRemoveOb, selectedOBs },
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});

		// this.containerRatingsService.addToBeSynched("observation", obActionReq);
		this.selectedOBList.emit(obAction);
	}

	saveAndClear() {
		const data = {
			candidateId: this.candidateId.value,
			comment: "",
		} as ResetOB;

		if (this.inDraftMode) {
			this.newOBList = [];

			this.resetRepeat = this.repeat?.getRawValue() + 1;

			this.maneuverForm.setValue(this.tempFrom);
			this.cd.detectChanges();

			this.repeat?.patchValue(this.resetRepeat);

			const record: DraftItem | undefined = this.containerRatingsService.draft;
			const cr = this.getSafeCandidateRating(record);

			const addRemoveObs = cr.addRemoveOb;
			const selectedOBsOriginal = cr.selectedOBs;

			const filteredAddRemoveObs = addRemoveObs.map((a: any) =>
				a.candidateId === this.candidateId.value && a.maneuverCode === this.maneuverCode.value
					? { ...a, isRepeat: true }
					: a,
			);

			const options = this.sharedService.dropDownOptionsManeuver;

			const filteredSelectedOBs = selectedOBsOriginal.map((a: any) => {
				const maneuver = options.find((e: any) => a.maneuverCode.includes(e.code));

				return a.candidateId === this.candidateId.value && maneuver?.code === this.maneuverCode.value
					? { ...a, isRepeat: true }
					: a;
			});

			const obAction = {
				maneuver:
					this.repeat?.getRawValue() != 0
						? `R${this.repeat?.getRawValue()} ${!this.isCabinCrewForm ? this.maneuver.value : ""}`
						: `${!this.isCabinCrewForm ? this.maneuver.value : ""}`,
				ob: "REPEAT",
				remarks: "REPEAT",
				action: false,
				instructorComment: "",
			} as OBComments;

			const temRate = { ...cr.temRate };
			if (this.candidateId.value && this.assessmentId.value) {
				delete temRate[`${this.candidateId.value}.${this.assessmentId.value}`];
			}

			const resetManeuvers = [
				...cr.resetManeuvers,
				{
					traineeAssessmentId: this.traineeAssessmentId.value,
					maneuverCode: this.maneuverCode.value,
					candidateId: this.candidateId.value,
					comment: "",
					count: this.resetRepeat,
					createdAt: new Date().toISOString(),
				},
			];

			this.containerRatingsService.updateSelectedDraft({
				candidateRating: {
					...cr,
					resetManeuvers,
					temRate,
					addRemoveOb: filteredAddRemoveObs,
					selectedOBs: filteredSelectedOBs,
				},
				lastUpdatedDate: new Date().toISOString(),
				continuePage: 2,
			});

			this.selectedOBList.emit(obAction);
		}
	}

	next() {
		const m = this.maneuver.value;
		this.tabIndex.selectedIndex = m && this.sharedService.dropDownOptionsManeuver.indexOf(m) + 1;
	}

	back() {
		const m = this.maneuver.value;
		this.tabIndex.selectedIndex = m && this.sharedService.dropDownOptionsManeuver.indexOf(m) - 1;
	}

	// Convenience to check + gate when to show the error
	get showTemError(): boolean {
		if (!this.isSafetyOutcomeVisible) return false;
		const c = this.safetyOutcome;
		if (!c) return false;

		// Only show when enabled and invalid, after interaction
		const interacted = c.touched || this.maneuverForm.touched || this.maneuverForm.dirty;
		return c.enabled && interacted && c.hasError("disallowedValue");
	}

	// The actual message (easy to change later)
	get temErrorText(): string {
		return "TEM Safety Outcome is required when there is at least one active OB.";
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}
}
